import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { ClockStopWatchIcon, PresentationChart02Icon } from '@/assets/icons';
import {
  Badge,
  InlineTabs,
  InlineTabsList,
  InlineTabsTrigger,
  IntakeScheduleCard,
  TabsContent,
} from '@/components/common';

import { DEFAULT_TIME_ZONE } from '@/constants';
import { getLocalHourFromCanonical, getTimezoneOffset } from '@/helpers';

import { JOB_SCHEDULE_HOURS } from '@/features/regulatory-intake/constants';
import { TabViewMode } from '@/features/regulatory-intake/enums';

import {
  useJobManualTriggerMutation,
  useSourceDetails,
} from '@/features/regulatory-intake/intake-list/hooks';

import { Jobs } from './Jobs';
import { Notifications } from './Notifications';

const IntakeList = () => {
  const currentTab = location.hash.replace('#', '') || TabViewMode.Notifications;
  const navigate = useNavigate();

  const { mutateAsync: jobManualTrigger, isPending } = useJobManualTriggerMutation();
  const { data: sourceDetails } = useSourceDetails();
  const intervals = useMemo(() => sourceDetails?.intervals || JOB_SCHEDULE_HOURS, [sourceDetails]);

  const onJobManualTrigger = async () => {
    await jobManualTrigger();
  };

  const localScheduleHours = intervals.map((h) =>
    getLocalHourFromCanonical(h, DEFAULT_TIME_ZONE, new Date()),
  );
  const scheduleLabel =
    `${localScheduleHours.map((hour) => String(hour).padStart(2, '0')).join(':00 & ')}:00 ` +
    `(${getTimezoneOffset(new Date())}) Daily`;

  return (
    <div className="flex flex-col justify-start items-center gap-2">
      <div className="w-full flex flex-row justify-between items-center">
        <div className="flex flex-col justify-start items-start">
          <div className="flex flex-row justify-start items-center gap-3">
            <PresentationChart02Icon className="size-8 text-neutral-6" />
            <span className="font-bold text-display text-neutral-9">Intake Jobs</span>
          </div>
          <span className="font-medium text-body-base text-neutral-7 mt-3 mb-4">
            Latest Jobs Overview & Tracking
          </span>
          <Badge border variant="blue" mode="light" className="px-3">
            <ClockStopWatchIcon className="size-4" />
            <span className="text-body-sm font-semibold ml-2">{scheduleLabel}</span>
          </Badge>
        </div>
        <IntakeScheduleCard
          title={sourceDetails?.source}
          email={sourceDetails?.emailAddress}
          lastJobTime={
            sourceDetails?.lastJobStartTime ? new Date(sourceDetails.lastJobStartTime) : undefined
          }
          nextJobTime={
            sourceDetails?.nextJobStartTime ? new Date(sourceDetails.nextJobStartTime) : undefined
          }
          className="max-w-[520px] w-fit min-w-[460px] max-h-[184px] min-h-[160px] h-fit"
          onTrigger={onJobManualTrigger}
          isRunning={isPending}
        />
      </div>
      <div className="w-full">
        <InlineTabs
          value={currentTab}
          onValueChange={(value) => {
            navigate({ hash: value }, { replace: true });
          }}
          className="w-full !gap-0"
        >
          <InlineTabsList className="w-full !gap-0">
            <InlineTabsTrigger value={TabViewMode.Notifications} className="w-[200px] !py-0 mb-4">
              Regulatory Notifications
            </InlineTabsTrigger>
            <InlineTabsTrigger value={TabViewMode.Jobs} className="w-[200px] !py-0 mb-4">
              Jobs
            </InlineTabsTrigger>
          </InlineTabsList>
          <TabsContent value={TabViewMode.Notifications} className="mt-4">
            <Notifications />
          </TabsContent>
          <TabsContent value={TabViewMode.Jobs} className="mt-4">
            <Jobs />
          </TabsContent>
        </InlineTabs>
      </div>
    </div>
  );
};

export default IntakeList;
